import { Injectable, inject } from '@angular/core';
import { Api } from '../api/api';
import {
  adminUserMlpControllerGetAllUserMlp,
  adminUserMlpControllerGetUserMlpById,
  adminUserMlpControllerCreateUserMlp,
  adminUserMlpControllerUpdateUserMlp,
  adminUserMlpControllerDeleteUserMlp,
  adminUserMlpControllerImportCsv,
} from '../api/functions';
import type {
  UserMlpResponseDto,
  PaginatedUserMlpResponseDto,
  CreateUserMlpDto,
  UpdateUserMlpDto,
} from '../api/models';

/**
 * Import CSV result interface
 */
export interface ImportCsvResult {
  message: string;
  created: number;
  updated: number;
  errors: string[];
  total: number;
}

/**
 * User MLP API Service
 * Wrapper around generated API functions for User MLP management
 */
@Injectable({ providedIn: 'root' })
export class UserMlpApiService {
  private readonly api = inject(Api);

  /**
   * Get all User MLP entries with pagination
   */
  async listUserMlp(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }): Promise<PaginatedUserMlpResponseDto> {
    return this.api.invoke(adminUserMlpControllerGetAllUserMlp, {
      page: params?.page,
      limit: params?.limit,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder as any,
      search: params?.search,
    });
  }

  /**
   * Get single User MLP entry by ID
   */
  async getUserMlp(id: string): Promise<UserMlpResponseDto> {
    return this.api.invoke(adminUserMlpControllerGetUserMlpById, { id });
  }

  /**
   * Create new User MLP entry
   */
  async createUserMlp(data: CreateUserMlpDto): Promise<UserMlpResponseDto> {
    return this.api.invoke(adminUserMlpControllerCreateUserMlp, {
      body: data,
    });
  }

  /**
   * Update User MLP entry
   */
  async updateUserMlp(id: string, data: UpdateUserMlpDto): Promise<UserMlpResponseDto> {
    return this.api.invoke(adminUserMlpControllerUpdateUserMlp, {
      id,
      body: data,
    });
  }

  /**
   * Delete User MLP entry
   */
  async deleteUserMlp(id: string): Promise<void> {
    await this.api.invoke(adminUserMlpControllerDeleteUserMlp, { id });
  }

  /**
   * Import User MLP entries from CSV file
   */
  async importCsv(file: File): Promise<ImportCsvResult> {
    const result = await this.api.invoke(adminUserMlpControllerImportCsv, {
      body: { file },
    });

    return {
      message: result.message ?? 'Import completed',
      created: result.created ?? 0,
      updated: result.updated ?? 0,
      errors: result.errors ?? [],
      total: result.total ?? 0,
    };
  }
}
